import { create } from 'zustand';
import type { ToastProps } from '@/app/_components/ui/toast';

export type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
};

export const TOAST_LIMIT = 1;
export const TOAST_REMOVE_DELAY = 1000000;

let count = 0;

export function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (
  toastId: string,
  removeToast: (id: string) => void,
) => {
  if (toastTimeouts.has(toastId)) return;

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    removeToast(toastId);
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

interface ToastState {
  toasts: ToasterToast[];
  addToast: (toast: ToasterToast) => void;
  updateToast: (id: string, toast: Partial<ToasterToast>) => void;
  dismissToast: (toastId?: string) => void;
  removeToast: (toastId?: string) => void;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (toast) =>
    set((state) => ({
      toasts: [toast, ...state.toasts].slice(0, TOAST_LIMIT),
    })),

  updateToast: (id, toast) =>
    set((state) => ({
      toasts: state.toasts.map((t) => (t.id === id ? { ...t, ...toast } : t)),
    })),

  dismissToast: (toastId) => {
    set((state) => ({
      toasts: state.toasts.map((t) =>
        t.id === toastId || toastId === undefined ? { ...t, open: false } : t,
      ),
    }));

    if (toastId) addToRemoveQueue(toastId, get().removeToast);
    else
      get().toasts.forEach((toast) =>
        addToRemoveQueue(toast.id, get().removeToast),
      );
  },

  removeToast: (toastId) =>
    set((state) => ({
      toasts:
        toastId === undefined
          ? []
          : state.toasts.filter((t) => t.id !== toastId),
    })),
}));
